import React from 'react';
import { Calendar, ChevronDown, Filter, Search, SearchX } from 'lucide-react';

export const palette = {
  navy: '#062B55',
  navyStrong: '#041f3d',
  navySoft: '#0B3C72',
  accent: '#6FA8F7',
  accentLight: '#9FC4FF',
  slate: '#94A3B8',
  slateDeep: '#64748B',
  border: '#E2E8F0',
  surfaceTint: '#F8FAFC',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  orange: '#F97316',
  greenSoft: '#DCFCE7',
  blueSoft: '#DBEAFE',
  redSoft: '#FEE2E2',
  amberSoft: '#FEF9C3',
  purpleSoft: '#EDE9FE',
};

export const chartColors = {
  navy: palette.navy,
  red: palette.danger,
  green: palette.success,
  amber: palette.warning,
  blue: palette.accent,
  slate: palette.slate,
};

export const chartBaseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 220,
  },
  plugins: {
    legend: {
      display: false,
      labels: {
        color: palette.slateDeep,
        font: { family: 'Inter', size: 11, weight: '500' },
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 8,
        boxHeight: 8,
        padding: 14,
      },
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#1E293B',
      bodyColor: '#1E293B',
      borderColor: palette.border,
      borderWidth: 1,
      titleFont: { family: 'Inter', size: 12, weight: '600' },
      bodyFont: { family: 'JetBrains Mono', size: 13, weight: '500' },
      padding: 12,
    },
  },
};

export function formatLakhs(value) {
  return `₹${(value / 100000).toFixed(2)}L`;
}

export function formatRupees(value) {
  return `₹${Math.round(value).toLocaleString('en-IN')}`;
}

export function toneAccent(tone) {
  switch (tone) {
    case 'red':
      return { line: palette.danger, soft: palette.redSoft, text: '#991B1B' };
    case 'orange':
      return { line: palette.warning, soft: '#FEF3C7', text: '#92400E' };
    case 'amber':
      return { line: palette.warning, soft: palette.amberSoft, text: '#92400E' };
    case 'green':
      return { line: palette.success, soft: palette.greenSoft, text: '#166534' };
    case 'blue':
      return { line: palette.accent, soft: palette.blueSoft, text: '#1D4ED8' };
    case 'purple':
      return { line: '#8B5CF6', soft: palette.purpleSoft, text: '#5B21B6' };
    case 'indigo':
      return { line: '#6366F1', soft: '#E0E7FF', text: '#4338CA' };
    default:
      return { line: palette.slateDeep, soft: '#F1F5F9', text: '#334155' };
  }
}

export function SectionTitle({ children, dot = palette.accent, className = '' }) {
  return (
    <div className={`section-label ${className}`} style={{ '--label-dot': dot }}>
      {children}
    </div>
  );
}

export function KpiCard({
  label,
  value,
  sub,
  dot = palette.accent,
  valueColor = dot,
  progress,
  progressColor = valueColor,
  pill,
}) {
  return (
    <div className="kpi-card-wrapper">
      <div className="kpi-label">
        <span className="kpi-dot" style={{ background: dot }} />
        {label}
      </div>
      <div className="kpi-value" style={{ color: valueColor }}>
        {value}
      </div>
      <div className="kpi-sub">{sub}</div>
      {pill ? (
        <div
          className="mt-3 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold"
          style={{ backgroundColor: `${dot}14`, color: valueColor }}
        >
          {pill}
        </div>
      ) : null}
      {typeof progress === 'number' ? (
        <div className="mt-3 h-1 rounded-full bg-slate-100">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.max(0, Math.min(progress, 100))}%`, background: progressColor }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * ChipStrip – iOS-style segmented-control filter strip.
 * `groups` is an array of { label, value, onChange, options: [{ value, label }] }.
 * Each group renders a labelled row of chip buttons separated by a divider.
 */
export function ChipStrip({ groups, className = '' }) {
  return (
    <div className={`chip-strip ${className}`}>
      {groups.map((group, gi) => (
        <React.Fragment key={group.label}>
          {gi > 0 && <span className="chip-strip-divider" />}
          <span className="chip-strip-label">{group.label}</span>
          <div className="flex flex-wrap gap-1.5">
            {group.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="chip-strip-chip"
                data-active={group.value === opt.value ? 'true' : undefined}
                onClick={() => group.onChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function FilterBar({ children, activeLabel, className = '' }) {
  return (
    <div className={`card-surface flex flex-col gap-2 p-4 sm:flex-row sm:flex-wrap sm:items-center ${className}`}>
      {children}
      {activeLabel ? (
        <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-600">
          Filtered: {activeLabel}
        </span>
      ) : null}
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options, className = '' }) {
  return (
    <label className={`flex min-w-[160px] flex-col gap-1.5 ${className}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full appearance-none rounded-lg border border-app-border bg-white px-3.5 py-2.5 pr-9 text-[13px] text-slate-800 outline-none transition-[border-color,box-shadow] duration-200 focus:border-accent focus:ring-2 focus:ring-accent/20"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </label>
  );
}

export function SearchField({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  label = 'Search',
}) {
  return (
    <label className={`flex min-w-[220px] flex-col gap-1.5 ${className}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-app-border bg-white py-2.5 pl-10 pr-3.5 text-[13px] text-slate-700 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
      </div>
    </label>
  );
}

export function AlertStrip({ icon = '', title, body }) {
  return (
    <div className="card-surface flex items-start gap-3 border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900">
      <div className="text-lg leading-none">{icon}</div>
      <div className="leading-relaxed">
        <strong className="font-semibold">{title}</strong>{' '}
        <span className="text-amber-800">{body}</span>
      </div>
    </div>
  );
}

export function InsightCard({ tone, title, exposure, body, actionTitle, action, chips, children }) {
  const accent = toneAccent(tone);
  return (
    <div className="card-surface rounded-2xl p-6">
      {/* Title row with tone dot */}
      <div className="flex items-start gap-2.5">
        <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: accent.line }} />
        <div className="text-[14px] font-bold leading-snug text-slate-800">{title}</div>
      </div>
      {/* Exposure as subtle pill */}
      {exposure ? (
        <div className="mt-2.5 pl-[18px]">
          <span
            className="inline-block rounded-md px-2.5 py-1 text-[11px] font-semibold leading-tight"
            style={{ backgroundColor: accent.soft, color: accent.text }}
          >
            {exposure}
          </span>
        </div>
      ) : null}
      {/* Body */}
      <p className="mt-3 pl-[18px] text-[13px] leading-[1.65] text-slate-600">{body}</p>
      {/* Chips */}
      {chips?.length ? (
        <div className="mt-3 flex flex-wrap gap-2 pl-[18px]">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-md px-2 py-1 text-[10px] font-semibold"
              style={{ backgroundColor: accent.soft, color: accent.text }}
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}
      {/* Metrics / custom children */}
      {children ? <div className="mt-3 pl-[18px]">{children}</div> : null}
      {/* Action block — clean recessed panel */}
      {(actionTitle || action) ? (
        <div className="mt-4 ml-[18px] rounded-xl bg-slate-50 px-4 py-3 text-[12px] leading-[1.6] text-slate-600">
          {actionTitle ? <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-500">{actionTitle}</div> : null}
          {action ? <div className={actionTitle ? 'mt-1' : ''}>{action}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * InsightBanner – navy gradient header for the Executive Root Cause Insights section.
 * `chips` is an optional array of summary strings shown as pills.
 */
export function InsightBanner({ chips = [] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-app-border">
      <div
        style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1e3a5f 55%, #2d5986 100%)' }}
        className="px-5 py-4 sm:px-6 sm:py-5"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm font-semibold text-white">Executive Root Cause Insights</div>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {chips.map((chip) => (
                <span key={chip} className="rounded-full bg-white/12 px-3 py-1 text-[11px] font-semibold text-white">
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SortButton({ label, sortKey, sortState, onSort, className = '' }) {
  const isActive = sortState?.key === sortKey;
  const icon = isActive ? (sortState.dir === 'asc' ? '↑' : '↓') : '↕';
  return (
    <button type="button" className={`table-sort ${className}`} onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      <span className={isActive ? 'text-slate-800' : 'text-slate-300'}>{icon}</span>
    </button>
  );
}

export function EmptyState({ title = 'No data for selected filters', icon = 'filter', className = '' }) {
  const Icon = icon === 'search' ? SearchX : Filter;
  return (
    <div className={`flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-app-border bg-slate-50/80 px-6 text-center ${className}`}>
      <Icon className="h-5 w-5 text-slate-300" />
      <div className="text-[13px] text-slate-400">{title}</div>
    </div>
  );
}

export function PeriodUnavailableState({ monthLabel = "Dec'25", className = '' }) {
  return (
    <div className={`card-surface flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-app-border bg-white px-6 text-center ${className}`}>
      <Calendar className="h-6 w-6 text-slate-300" />
      <div className="mt-3 text-[17px] font-semibold text-slate-600">Data not available for this period</div>
      <div className="mt-1 text-[13px] text-slate-400">{monthLabel} data is being processed.</div>
    </div>
  );
}

export function SubTabPanel({ tabs, activeTab, onTabChange, variant = '', children }) {
  return (
    <div className="card-surface overflow-hidden">
      <div className="sub-tab-nav">
        {tabs.map((tab, i) => (
          <button
            key={tab.key || i}
            type="button"
            onClick={() => onTabChange(i)}
            className={`sub-tab-btn${activeTab === i ? ` sub-tab-btn-active${variant ? ` sub-tab-${variant}` : ''}` : ''}`}
          >
            {tab.label}{tab.count != null ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

export function ProgressBar({ value, max = 100, color = palette.success, label, className = '' }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {label ? <span className="min-w-[60px] text-[11px] font-semibold text-slate-500">{label}</span> : null}
      <div className="progress-bar-track flex-1">
        <div className="progress-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="data-value min-w-[40px] text-right text-[11px] font-semibold text-slate-600">
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

export function DomainBadge({ domain, className = '' }) {
  return (
    <span className={`domain-badge domain-badge-${domain.toLowerCase()} ${className}`}>
      {domain}
    </span>
  );
}

export function LoadMoreButton({ onClick, remaining }) {
  return (
    <button type="button" className="load-more-btn" onClick={onClick}>
      Load {remaining != null ? `${Math.min(remaining, 20)} more` : 'more'} sites
    </button>
  );
}

export function ScoreCell({ value, className = '' }) {
  let tier = 'score-cell-0';
  if (value >= 40) tier = 'score-cell-40';
  else if (value >= 30) tier = 'score-cell-30';
  else if (value >= 20) tier = 'score-cell-20';
  else if (value >= 15) tier = 'score-cell-15';
  else if (value >= 10) tier = 'score-cell-10';
  else if (value >= 5) tier = 'score-cell-5';
  return (
    <span className={`score-cell ${tier} ${className}`}>
      {typeof value === 'number' ? value : value}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const tone = priority === 'P1'
    ? toneAccent('red')
    : priority === 'P2'
      ? toneAccent('orange')
      : toneAccent('blue');

  return (
    <span
      className="inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.06em]"
      style={{ backgroundColor: tone.soft, color: tone.text }}
    >
      {priority}
    </span>
  );
}
